import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useAuthStore } from '@/stores';
import { PageLoader } from '@/components/ui';
import { hasE2eBypassSession } from './e2eAuthBypass';

interface PublicRouteProps {
  children: ReactNode;
}

export function PublicRoute({ children }: PublicRouteProps) {
  const zustandIsAuthenticated = useAuthStore(state => state.isAuthenticated);
  const msalIsAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const location = useLocation();

  // Wait for MSAL to finish any in-progress operations before making auth decisions
  if (inProgress !== InteractionStatus.None) {
    return <PageLoader />;
  }

  // Check authentication from either MSAL or Zustand (or the e2e-only bypass,
  // see ProtectedRoute) so a signed-in e2e browser leaves /login too.
  const isAuthenticated =
    msalIsAuthenticated || zustandIsAuthenticated || hasE2eBypassSession();

  if (isAuthenticated) {
    // Redirect to the page they came from, or clients by default
    const from = (location.state as { from?: { pathname: string } })?.from
      ?.pathname;
    return <Navigate to={from || '/clients'} replace />;
  }

  return <>{children}</>;
}
